import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { observer } from 'mobx-react'
import { Card, Segmented, Skeleton } from 'antd'
import { theme } from 'antd'
import { BarChartOutlined } from '@ant-design/icons'
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell, Tooltip as RechartsTooltip } from 'recharts'
import PushlyIcon from '@pushly/aqe/lib/components/icons/pushly-icon'
import { useAppState } from '../hooks/use-mobile-service'
import { useInsightsData, InsightsDatePreset } from '../../hooks/use-insights-data'
import { formatCompactDisplay } from '../notifications/shared/notification-constants'
import { StatsRow } from '../components/stats-row/stats-row'
import { MobileEmptyState } from '../components/mobile-empty-state'
import { NotificationCard } from '../home/notification-card/notification-card'
import { NotificationQuickSheet } from '../notification-quick-sheet/notification-quick-sheet'
import './insights-view.scss'

type ActiveMetric = 'impressions' | 'clicks' | 'ctr'

const DATE_PRESETS: Array<{ label: string; value: InsightsDatePreset }> = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: '7 Days', value: 'last_7_days' },
    { label: '30 Days', value: 'last_30_days' },
]

function formatDate(dateStr: string): string {
    if (!dateStr) return ''
    const parts = dateStr.split('-')
    if (parts.length !== 3) return dateStr
    const month = parseInt(parts[1], 10)
    const day = parseInt(parts[2], 10)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${months[month - 1]} ${day}`
}

export const InsightsView: React.FC = observer(() => {
    const appState = useAppState()
    const { token } = theme.useToken()

    const [preset, setPreset] = useState<InsightsDatePreset>('last_7_days')
    const [activeMetric, setActiveMetric] = useState<ActiveMetric>('impressions')
    const [quickSheetNotifId, setQuickSheetNotifId] = useState<string | null>(null)

    const { summary, daily, highlightDate, topNotifications, loading } = useInsightsData(preset)

    // Keep chart overlay visible slightly after loading completes
    // so the new chart has time to render before the overlay fades out
    const [chartLoading, setChartLoading] = useState(false)
    const chartLoadingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (loading) {
            // Show overlay immediately when loading starts
            if (chartLoadingTimer.current) clearTimeout(chartLoadingTimer.current)
            setChartLoading(true)
        } else {
            // Delay hiding overlay by 420ms to let the chart render
            chartLoadingTimer.current = setTimeout(() => {
                setChartLoading(false)
            }, 420)
        }
        return () => {
            if (chartLoadingTimer.current) clearTimeout(chartLoadingTimer.current)
        }
    }, [loading])

    const domainId = appState.currentDomain?.id

    const handlePresetChange = useCallback((value: string | number) => {
        setPreset(value as InsightsDatePreset)
    }, [])

    const handleMetricTap = useCallback((metric: ActiveMetric) => {
        setActiveMetric(metric)
    }, [])

    const handleNotificationTap = useCallback((notifId: string) => {
        requestAnimationFrame(() => {
            setQuickSheetNotifId(notifId)
        })
    }, [])

    const chartData = useMemo(() => {
        return daily.map((row) => ({
            date: formatDate(row.date),
            value: activeMetric === 'ctr' ? row.ctrDecimal * 100 : row[activeMetric],
            highlighted: highlightDate ? row.date === highlightDate : false,
        }))
    }, [daily, activeMetric, highlightDate])

    const isSingleDay = preset === 'today' || preset === 'yesterday'

    // Determine if chart has any non-zero values (avoid rendering a flat line at 0)
    const hasChartActivity = useMemo(() => chartData.some((d) => d.value > 0), [chartData])

    // Determine if the entire view has no meaningful data (empty state)
    const hasNoData = !loading && summary && summary.impressions === 0 && summary.clicks === 0

    // Chart colors — Recharts SVG attributes don't support CSS variables,
    // so we resolve hex values here. These must match the KPI tile colors.
    // Source: AQE design tokens ($color-primary-100, $color-tertiary-100, theme colorPrimary)
    const CHART_COLORS: Record<ActiveMetric, string> = useMemo(
        () => ({
            impressions: '#464d53', // $color-primary-100 (slate)
            clicks: '#722ed1', // $color-tertiary-100 (purple)
            ctr: token.colorPrimary, // teal from antd theme
        }),
        [token.colorPrimary],
    )

    const chartColor = CHART_COLORS[activeMetric]

    // Sort top notifications by the active metric, limit to 25
    const sortedTopNotifications = useMemo(() => {
        return [...topNotifications]
            .sort((a, b) => {
                switch (activeMetric) {
                    case 'impressions':
                        return b.impressions - a.impressions
                    case 'clicks':
                        return b.clicks - a.clicks
                    case 'ctr':
                        return b.ctrDecimal - a.ctrDecimal
                }
            })
            .slice(0, 25)
    }, [topNotifications, activeMetric])

    if (loading && !summary) {
        return (
            <div className="insights-view">
                <Segmented
                    options={DATE_PRESETS}
                    value={preset}
                    onChange={handlePresetChange}
                    block
                    className="insights-view__presets"
                />
                <StatsRow loading impressions={{ display: '--' }} clicks={{ display: '--' }} ctr="--" />
                <Skeleton active paragraph={{ rows: 6 }} />
            </div>
        )
    }

    return (
        <div className="insights-view">
            {/* Date preset selector */}
            <Segmented
                options={DATE_PRESETS}
                value={preset}
                onChange={handlePresetChange}
                block
                className="insights-view__presets"
            />

            {/* KPI cards as metric selectors */}
            <StatsRow
                impressions={{ display: summary ? formatCompactDisplay(summary.impressions) : '--' }}
                clicks={{ display: summary ? formatCompactDisplay(summary.clicks) : '--' }}
                ctr={summary ? `${(summary.ctrDecimal * 100).toFixed(2)}%` : '--'}
                activeMetric={activeMetric}
                onMetricTap={handleMetricTap}
            />

            {/* Chart — bar for single-day (highlighted active day), area for multi-day */}
            {hasChartActivity ? (
                <Card size="small" className="insights-view__chart" styles={{ body: { padding: 0 } }}>
                    {chartLoading && (
                        <div className="insights-view__chart-overlay">
                            <span className="insights-view__chart-loading-icon">
                                <PushlyIcon aria-hidden="true" />
                            </span>
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height={180}>
                        {isSingleDay ? (
                            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                <RechartsTooltip
                                    labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ''}
                                    formatter={(value: number) =>
                                        activeMetric === 'ctr' ? `${value.toFixed(2)}%` : value.toLocaleString()
                                    }
                                />
                                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                                    {chartData.map((entry, idx) => (
                                        <Cell key={idx} fill={chartColor} opacity={entry.highlighted ? 1 : 0.25} />
                                    ))}
                                </Bar>
                            </BarChart>
                        ) : (
                            <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                <defs>
                                    <linearGradient id="insights-gradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={chartColor} stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <RechartsTooltip
                                    labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ''}
                                    formatter={(value: number) =>
                                        activeMetric === 'ctr' ? `${value.toFixed(2)}%` : value.toLocaleString()
                                    }
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={chartColor}
                                    strokeWidth={2}
                                    fill="url(#insights-gradient)"
                                />
                            </AreaChart>
                        )}
                    </ResponsiveContainer>
                </Card>
            ) : loading ? (
                <Card size="small" className="insights-view__chart" styles={{ body: { padding: 0 } }}>
                    <div className="insights-view__chart-overlay">
                        <span className="insights-view__chart-loading-icon">
                            <PushlyIcon aria-hidden="true" />
                        </span>
                    </div>
                </Card>
            ) : null}

            {/* Empty state — shown when no activity in the selected time period */}
            {hasNoData && (
                <MobileEmptyState
                    icon={<BarChartOutlined style={{ fontSize: 32, color: 'var(--color-gray-dark)' }} />}
                    description="No activity for this time period"
                    message="Try selecting a different date range to see notification performance data."
                />
            )}

            {/* Top Notifications */}
            {sortedTopNotifications.length > 0 && (
                <div className="insights-view__top">
                    <div className="insights-view__top-header">Top Notifications</div>
                    <div className="insights-view__top-list">
                        {sortedTopNotifications.map((notif) => (
                            <NotificationCard
                                key={notif.notificationId}
                                notification={{
                                    id: 0,
                                    notificationId: notif.notificationId,
                                    title: notif.title,
                                    body: notif.body,
                                    imageUrl: notif.imageUrl,
                                    status: 'COMPLETED',
                                    source: notif.source,
                                    sendTime: notif.sendTime ?? '',
                                    audience: notif.audience
                                        ? {
                                              segments: notif.audience.targeted?.map((t) => ({
                                                  id: t.value,
                                                  name: t.label,
                                                  segment_type: t.type,
                                                  is_default: t.label === 'All Push Subscribers',
                                              })),
                                              excluded_segments: notif.audience.excluded?.map((t) => ({
                                                  id: t.value,
                                                  name: t.label,
                                                  segment_type: t.type,
                                                  is_default: false,
                                              })),
                                          }
                                        : undefined,
                                    clicks: notif.clicks,
                                    ctrDecimal: notif.ctrDecimal,
                                }}
                                hideStatus
                                metricDisplay={{
                                    metric: activeMetric,
                                    value:
                                        activeMetric === 'ctr'
                                            ? `${(notif.ctrDecimal * 100).toFixed(2)}%`
                                            : activeMetric === 'clicks'
                                            ? formatCompactDisplay(notif.clicks)
                                            : formatCompactDisplay(notif.impressions),
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            <NotificationQuickSheet
                notificationId={quickSheetNotifId}
                open={!!quickSheetNotifId}
                onClose={() => setQuickSheetNotifId(null)}
            />
        </div>
    )
})

InsightsView.displayName = 'InsightsView'
